/**
 * How a timestamp is turned into words.
 *
 * Every timestamp in this schema is a `timestamp without time zone` holding a UTC
 * wall clock, and the API returns it as an instant (`...Z`) — see
 * `packages/api/src/db/client.ts`. What differs is what that instant MEANS, and
 * the two meanings cannot share a formatter:
 *
 *   a MOMENT   — a flight's `date`. It happened at a time of day, and the answer
 *                he wants is the clock he flew by, so render it where he is.
 *
 *   a DAY      — a meter reading's `as_of`, a co-owner leg's date. These are
 *                calendar days picked out of a `<input type="date">`; the time of
 *                day is packing, not information. Rendering one as a local instant
 *                walks it backwards everywhere west of Greenwich — midnight UTC is
 *                7pm the previous evening in Chicago, so a reading taken on the
 *                11th prints "Sep 10". That is the whole reason this file exists.
 *
 * So a day is formatted IN UTC, which is the zone its digits were written in, and
 * a moment is formatted locally. Neither is a global timezone setting: this repo
 * has been bitten twice by changing the zone for everything at once (#8 and the
 * driver parsers), and the fix both times was to tell the two kinds apart.
 *
 * Pinned by `dates.test.ts`, which asserts both ends of a UTC day so the tests
 * fail in a zone on either side of Greenwich. CI runs the suite under
 * `Pacific/Chatham` and `America/Chicago` for exactly that.
 */

const DAY_FORMAT: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };

function parse(value: string | Date | null | undefined): Date | null {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * A value that means a calendar DAY — "Sep 11, 2026". Formatted in UTC, so the
 * day printed is the day stored no matter where the browser is.
 */
export function formatCalendarDay(value: string | Date | null | undefined): string | null {
  const d = parse(value);
  return d && d.toLocaleDateString(undefined, { ...DAY_FORMAT, timeZone: 'UTC' });
}

/**
 * A value that means a MOMENT — the viewer's local date for an instant. For a
 * flight, which happened at a time of day in a place.
 */
export function formatInstant(value: string | Date | null | undefined): string | null {
  const d = parse(value);
  return d && d.toLocaleDateString(undefined, DAY_FORMAT);
}
